
import { mapState } from "vuex";
import JsFileDownload from "js-file-download";
import axios from "axios";
import { State } from "@/store";
import { isIOS, canNativeShare, nativeShare } from "@/utils";

import Message from "@/components/Message.vue";
import ReplyModal from "@/components/ReplyModal.vue";
import HomeContentClassic from "./HomeContentClassic.vue";
import HomeContentCustom from "./HomeContentCustom.vue";
import Loader from "@/components/Loader.vue";
import ReportContentPopin from "@/components/ReportContentPopin.vue";
import { ErrorStatus } from "@/typings/Error";

export default {
    name: "Home",
    components: {
        Message,
        HomeContentClassic,
        HomeContentCustom,
        Loader,
        ReportContentPopin,
        ReplyModal
    },
    data() {
        return {
            isGoingToAR: false,
            messageOverlay: {},
            isMessage: false,
            isReplyOpen: false,
            permissionError: false,
            displayReportContentPopin: false,
            isIOS: isIOS()
        };
    },
    computed: {
        contentBanned() {
            return this.qrcodeObject.xpBanned || this.qrcodeObject.projectBanned;
        },
        contentReported() {
            return this.qrcodeObject.xpReported || this.qrcodeObject.projectReported;
        },
        reportedOrBanned() {
            return this.contentReported || this.contentBanned;
        },
        noSavedExperience() {
            return Boolean(!this.qrcodeObject || this.qrcodeObject.isOpen);
        },
        isCommentingAllowed() {
            return this.qrcodeObject.isCommentingAllowed || false;
        },
        canEditExperience() {
            return (
                this.authenticated &&
                !this.noSavedExperience &&
                this.qrcodeObject?.experiences?.length &&
                this.qrcodeObject.experiences[0].ownerId === this.userId
            );
        },
        fullVideoUrl() {
            if (!this.videoUrl?.length) {
                return "";
            }

            return isIOS() ? this.videoUrl + "#t=0.5" : this.videoUrl;
        },
        adminUrl() {
            return (
                process.env.VUE_APP_ADMIN_URL +
                (this.$store.state.auth.sessionToken.length
                    ? "?session=" + this.$store.state.auth.sessionToken
                    : "")
            );
        },
        ...mapState({
            qrcodeObject: (state: State) => state.qrcode.qrcodeObject,
            videoUrl: (state: State) => state.qrcode.videoUrl,
            customHtml: (state: State) => state.qrcode.customHtml,
            customJs: (state: State) => state.qrcode.customJs,
            qrcodeError: (state: State) => state.qrcode.error,
            hasRecordedExperience: (state: State) => state.record.videoFile !== null,
            authenticated: (state: State) => state.auth.authenticated,
            userId: (state: State) => state.auth.userId
        })
    },
    async mounted() {
        // Load 8thwall if necessary
        /*         if (!parsed.searchObject.gyro && typeof XR === "undefined") {
            const scriptTag = document.createElement("script");
            scriptTag.setAttribute("async", true);
            // 8thWall Web - Replace the app key here with your own app key
            scriptTag.src =
                "//apps.8thwall.com/xrweb?appKey=acWlKxbHzojExphov96HSRIIuF68jClMEz7IPE2wf3mJ08QufHdpA6WYgwxahkTfyM3LHR";
            document.head.appendChild(scriptTag);
        } */
    },
    beforeUnmount() {
        Array.from(document.getElementsByClassName("banzai")).forEach(element => {
            element.classList.remove("expanded");
        });
    },
    methods: {
        checkCompatibility() {
            /*
            let isWasm;
            if (typeof WebAssembly === "object" && typeof WebAssembly.instantiate === "function") {
                const module = new WebAssembly.Module(
                    Uint8Array.of(0x0, 0x61, 0x73, 0x6d, 0x01, 0x00, 0x00, 0x00)
                );
                if (module instanceof WebAssembly.Module)
                    isWasm = new WebAssembly.Instance(module) instanceof WebAssembly.Instance;
            }
            */
            const navAgent = navigator.userAgent;
            const forbiddenStrings = ["FBAN", "FBAV"];
            const isAllowedApp = forbiddenStrings.findIndex(str => navAgent.includes(str)) === -1;
            const isVideo = "mediaDevices" in navigator && "getUserMedia" in navigator.mediaDevices;
            return isAllowedApp && isVideo;
        },
        showMessage(_isError, title, text, isCompatible = true, allowCopy = false) {
            this.messageOverlay = {
                isError: _isError,
                title: title,
                text: text,
                isCompliant: isCompatible,
                allowCopy
            };
            if (_isError) {
                console.error(text);
            }

            this.isMessage = true;
        },
        onError(status) {
            if (status === ErrorStatus.Timeout) {
                this.showMessage(
                    true,
                    this.$t("errors.timeout"),
                    this.$t("errors.videoConnectionProblems")
                );
            }
            if (status === ErrorStatus.LoadError) {
                this.showMessage(
                    true,
                    this.$t("errors.serverErrorTitle"),
                    this.$t("errors.videoConnectionProblems")
                );
            }
        },

        onServerError() {
            console.log("onServerError");
            this.showMessage(
                true,
                "Erreur serveur",
                "Problème de connexion à notre serveur QRCode"
            );
        },

        goToRecord() {
            this.$router.push("Record");
        },

        onVideoPick(event: Event) {
            const file = (event.target as HTMLInputElement).files[0];

            this.$store.dispatch("record/setRecordedVideo", {
                videoFile: file,
                videoName: file.name
            });

            if (this.qrcodeObject.fastUX) {
                this.$router.push("Upload");
            } else {
                this.$router.push("Record");
            }
        },

        openReplyVideoModal() {
            this.isReplyOpen = true;
        },

        async replyToVideo(message: string, firstName: string, lastName: string) {
            const url = new URL(process.env.VUE_APP_API_ROOT + "comments");

            const formData = new FormData();
            formData.append("qrcodeId", this.qrcodeObject.qrcodeId);
            formData.append("text", message);
            formData.append("firstName", firstName);
            formData.append("lastName", lastName);

            try {
                const result = await axios.post(url.href, formData);

                if (result.status === 200) {
                    this.showMessage(false, this.$t("success.done"), this.$t("reply.replySent"));
                }

                return result;
            } catch (error) {
                this.showMessage(
                    true,
                    this.$t("errors.serverErrorTitle"),
                    error?.message || this.$t("errors.videoConnectionProblems")
                );
                return error;
            }
        },

        goAr() {
            const isCompliant = this.checkCompatibility() as boolean;
            if (!isCompliant) {
                this.showMessage(
                    true,
                    this.$t("home.changeBrowser"),
                    this.$t("home.changeBrowserDetail"),
                    false
                );
                return;
            }

            let permissionPromise;

            if (typeof DeviceMotionEvent.requestPermission === "function") {
                console.log("request perm");
                permissionPromise = DeviceMotionEvent.requestPermission();
            } else {
                console.log("no request perm");
                permissionPromise = Promise.resolve("granted");
            }

            permissionPromise.then(response => {
                console.log("permissionPromise:" + response);
                if (response === "granted") {
                    Array.from(document.getElementsByClassName("banzai")).forEach(element => {
                        element.classList.add("expanded");
                    });
                    // display big loader after 1s of animation
                    setTimeout(() => (this.isGoingToAR = true), 1000);

                    // go to AR view after 1.5s
                    setTimeout(() => this.$router.push("AR"), 1500);
                } else {
                    this.permissionError = true;
                    this.showMessage(
                        true,
                        this.$t("general.permissionError"),
                        this.$t("general.motionPermissionRefused")
                    );
                }
            });
        },

        share() {
            const shareTitle = this.noSavedExperience
                ? this.$t("share.openTitle")
                : this.$t("share.burnedTitle");
            // experience name
            const shareText = this.noSavedExperience
                ? this.$t("share.openText")
                : this.qrcodeObject.experiences[0].name;

            if (canNativeShare()) {
                nativeShare(shareTitle, shareText, document.location.href.split("#")[0]);
            } else {
                this.showMessage(
                    false,
                    this.$t("general.share"),
                    `${shareText} ${document.location.href.split("#")[0]}`,
                    true,
                    true
                );
            }
        },

        downloadVideo() {
            axios({
                method: "GET",
                url: this.fullVideoUrl,
                responseType: "blob"
            }).then(response => {
                JsFileDownload(response.data, "stqr video.mp4", response.headers["content-type"]);
            });
        },

        switchLocale(locale) {
            this.$i18n.locale = locale;
        },

        openReportPopin() {
            document.body.classList.add("noscroll");
            this.displayReportContentPopin = true;
        },

        closeReportPopin() {
            document.body.classList.remove("noscroll");
            this.displayReportContentPopin = false;
        }
    }
};
